import {radialProgressiveBlur} from '@remotion/effects/radial-progressive-blur';
import {MacOSCursor} from '@remotion/mac-cursors';
import {Video} from '@remotion/media';
import {
	AbsoluteFill,
	Composition,
	Easing,
	HtmlInCanvas,
	Interactive,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const BasicsPlaybackRateInner = ({
	captureStyle,
	blurEdges,
}: {
	readonly captureStyle: React.CSSProperties | null;
	readonly blurEdges: boolean | null;
}) => {
	const {fps, width, height} = useVideoConfig();
	const frame = (useCurrentFrame() * 60) / fps;

	const screenCapture = (
		<>
			<Video
				name="Screen recording"
				premountFor={fps}
				muted
				effects={
					HtmlInCanvas.isSupported() || blurEdges === false
						? []
						: [
								radialProgressiveBlur({
									center: [0.25, 0.49],
									width: 0.85,
									height: 0.74,
									start: 0.42,
									startBlur: 0,
									endBlur: 32,
								}),
							]
				}
				src={
					'https://remotion.media/studio-close-ups/basics-showcase/changing-playbackrate-874793ccb8b6.mp4'
				}
				durationInFrames={(368 / 60) * fps}
				style={{
					position: 'absolute',
				}}
			/>
			<MacOSCursor
				name="Recorded cursor"
				premountFor={fps}
				cursor={interpolate(
					frame,
					[11, 19, 20, 23, 24, 26, 52, 56, 313, 316, 317],
					[
						'auto',
						'default',
						'auto',
						'custom',
						'col-resize',
						'auto',
						'default',
						'ew-resize',
						'auto',
						'custom',
						'auto',
					],
					{
						easing: Easing.step1,
						extrapolateLeft: 'clamp',
						extrapolateRight: 'clamp',
					},
				)}
				customCursor={
					'url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2032%2032%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url(%23shadow)%22%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22white%22%20stroke-width%3D%225%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22black%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22shadow%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2232%22%20height%3D%2232%22%20filterUnits%3D%22userSpaceOnUse%22%3E%0A%3CfeDropShadow%20dx%3D%220%22%20dy%3D%221.5%22%20stdDeviation%3D%221%22%20flood-color%3D%22black%22%20flood-opacity%3D%220.35%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E") 12 12, ew-resize'
				}
				style={{
					position: 'absolute',
					left: 0,
					top: 0,
					scale: interpolate(
						frame,
						[0, 73, 290, 350, 355],
						[4.6, 4.14, 4.6, 4.14, 4.6],
						{
							easing: Easing.step1,
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
					translate: interpolate(
						frame,
						[
							11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
							27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42,
							43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58,
							59, 60, 61, 63, 64, 66, 67, 69, 70, 71, 93, 94, 95, 96, 97, 98,
							99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111,
							112, 113, 116, 117, 120, 121, 122, 123, 125, 126, 128, 129, 131,
							132, 133, 134, 136, 137, 139, 140, 141, 142, 143, 145, 146, 147,
							148, 150, 151, 152, 153, 154, 155, 158, 159, 161, 162, 165, 166,
							169, 170, 171, 172, 174, 175, 177, 178, 180, 181, 182, 183, 185,
							186, 187, 188, 190, 191, 192, 193, 195, 196, 197, 198, 202, 203,
							204, 205, 206, 207, 209, 210, 211, 214, 215, 217, 218, 219, 220,
							221, 222, 224, 225, 227, 228, 232, 233, 236, 237, 238, 240, 241,
							243, 244, 245, 246, 248, 249, 250, 251, 252, 254, 255, 312, 313,
							314, 315, 316, 317, 318, 319, 320, 321, 322, 323, 324, 325, 329,
							330, 331, 332, 333, 334, 335, 336, 337, 338, 342, 343, 344, 345,
							346, 347, 350, 351,
						],
						[
							'3154.5578124999997px 1926.7710937499999px',
							'3079.9874999999997px 1921.6859375px',
							'3009.2625px 1900.0156249999998px',
							'2905.0796874999996px 1867.2046874999999px',
							'2827.22109375px 1836.06484375px',
							'2656.1945312499997px 1779.5171874999999px',
							'2462.3117187499997px 1699.8437499999998px',
							'2161.5148437499997px 1603.5312499999998px',
							'1877.8242187499998px 1508.94375px',
							'1691.4882812499998px 1458.128125px',
							'1549.6968749999999px 1413.4578124999998px',
							'1421.83125px 1377.93359375px',
							'1306.4898437499999px 1337.3421875px',
							'1220.5093749999999px 1307.2085937499999px',
							'1220.5093749999999px 1307.2085937499999px',
							'1109.534375px 1263.22109375px',
							'1049.1234375px 1238.06484375px',
							'1015.6835937499999px 1221.9828125px',
							'1001.5960937499999px 1213.39375px',
							'991.8929687499999px 1206.11640625px',
							'982.1898437499999px 1198.8390625px',
							'975.57734375px 1192.2265625px',
							'968.9648437499999px 1188.0398437499998px',
							'961.6874999999999px 1180.7624999999998px',
							'951.9843749999999px 1173.4851562499998px',
							'937.8968749999999px 1162.4703124999999px',
							'919.425px 1153.2164062499999px',
							'905.3375px 1142.2015625px',
							'871.89765625px 1122.38203125px',
							'853.4257812499999px 1110.0554687499998px',
							'834.9539062499999px 1097.72890625px',
							'820.86640625px 1089.13984375px',
							'806.77890625px 1080.55078125px',
							'797.07578125px 1073.2734375px',
							'787.37265625px 1068.421875px',
							'783.85078125px 1064.8999999999999px',
							'776.5734375px 1060.0484374999999px',
							'774.8124999999999px 1058.2875px',
							'769.9609374999999px 1055.86171875px',
							'758.065625px 1048.8179687499999px',
							'754.5437499999999px 1047.05703125px',
							'752.7828125px 1045.29609375px',
							'749.2609375px 1041.7742187499998px',
							'745.7390624999999px 1040.0132812499999px',
							'742.2171874999999px 1036.49140625px',
							'740.45625px 1034.73046875px',
							'737.5453124999999px 1033.2749999999999px',
							'737.5453124999999px 1031.5140625px',
							'737.5453124999999px 1031.5140625px',
							'736.2695312499999px 1031.5140625px',
							'736.2695312499999px 1030.16640625px',
							'736.2695312499999px 1030.16640625px',
							'735.0476562499999px 1028.40546875px',
							'735.0476562499999px 1028.40546875px',
							'735.0476562499999px 1027.18359375px',
							'735.0476562499999px 1027.18359375px',
							'735.0476562499999px 1025.92578125px',
							'733.6999999999999px 1025.92578125px',
							'733.6999999999999px 1025.92578125px',
							'734.2390624999999px 1025.92578125px',
							'734.2390624999999px 1025.92578125px',
							'735.4070312499999px 1025.92578125px',
							'736.7546874999999px 1025.92578125px',
							'738.1742187499999px 1025.92578125px',
							'739.4499999999999px 1025.92578125px',
							'740.7617187499999px 1025.92578125px',
							'742.2171874999999px 1025.92578125px',
							'743.70859375px 1025.92578125px',
							'743.70859375px 1025.92578125px',
							'745.1640624999999px 1025.92578125px',
							'745.1640624999999px 1025.92578125px',
							'746.4398437499999px 1025.92578125px',
							'746.4398437499999px 1025.92578125px',
							'747.6796874999999px 1025.92578125px',
							'747.6796874999999px 1025.92578125px',
							'748.8835937499999px 1025.92578125px',
							'750.1234374999999px 1025.92578125px',
							'751.5429687499999px 1025.92578125px',
							'753.034375px 1025.92578125px',
							'753.034375px 1025.92578125px',
							'755.7296875px 1025.92578125px',
							'755.7296875px 1025.92578125px',
							'759.6828125px 1025.92578125px',
							'759.6828125px 1025.92578125px',
							'763.8695312499999px 1025.92578125px',
							'763.8695312499999px 1025.92578125px',
							'768.128125px 1025.92578125px',
							'768.128125px 1025.92578125px',
							'772.8px 1025.92578125px',
							'772.8px 1025.92578125px',
							'778.4960937499999px 1025.92578125px',
							'778.4960937499999px 1025.92578125px',
							'781.7125px 1025.92578125px',
							'781.7125px 1025.92578125px',
							'783.0421875px 1025.92578125px',
							'783.0421875px 1025.92578125px',
							'784.2820312499999px 1025.92578125px',
							'785.665625px 1025.92578125px',
							'785.665625px 1025.92578125px',
							'788.253125px 1025.92578125px',
							'788.253125px 1025.92578125px',
							'793.01484375px 1025.92578125px',
							'793.01484375px 1025.92578125px',
							'794.5062499999999px 1025.92578125px',
							'794.5062499999999px 1025.92578125px',
							'797.0937499999999px 1025.92578125px',
							'797.0937499999999px 1025.92578125px',
							'801.0109375px 1025.92578125px',
							'801.0109375px 1025.92578125px',
							'803.99375px 1025.92578125px',
							'803.99375px 1025.92578125px',
							'809.6359375px 1025.92578125px',
							'809.6359375px 1025.92578125px',
							'814.1101562499999px 1025.92578125px',
							'814.1101562499999px 1025.92578125px',
							'818.27890625px 1026.5546875px',
							'818.27890625px 1026.5546875px',
							'822.66328125px 1027.2375px',
							'822.66328125px 1027.2375px',
							'825.28671875px 1027.2375px',
							'825.28671875px 1027.2375px',
							'828.34140625px 1028.675px',
							'828.34140625px 1028.675px',
							'829.6351562499999px 1028.675px',
							'829.6351562499999px 1028.675px',
							'832.29453125px 1028.675px',
							'832.29453125px 1028.675px',
							'833.5164062499999px 1029.2859374999998px',
							'833.5164062499999px 1029.2859374999998px',
							'835.9421874999999px 1029.8609374999999px',
							'835.9421874999999px 1029.8609374999999px',
							'839.0687499999999px 1031.4781249999999px',
							'839.0687499999999px 1031.4781249999999px',
							'841.9796875px 1031.60390625px',
							'841.9796875px 1031.60390625px',
							'844.7109374999999px 1031.60390625px',
							'844.7109374999999px 1031.60390625px',
							'847.8015624999999px 1033.04140625px',
							'847.8015624999999px 1033.04140625px',
							'849.275px 1033.04140625px',
							'849.275px 1033.04140625px',
							'851.7187499999999px 1033.04140625px',
							'852.9945312499999px 1033.04140625px',
							'854.3601562499999px 1033.04140625px',
							'854.3601562499999px 1033.04140625px',
							'855.6359375px 1033.04140625px',
							'855.6359375px 1033.04140625px',
							'856.98359375px 1034.3171874999998px',
							'858.3851562499999px 1034.33515625px',
							'858.3851562499999px 1034.33515625px',
							'859.6429687499999px 1034.33515625px',
							'859.6429687499999px 1034.33515625px',
							'860.7929687499999px 1034.33515625px',
							'860.7929687499999px 1034.33515625px',
							'862.01484375px 1034.33515625px',
							'862.01484375px 1034.33515625px',
							'863.3265624999999px 1034.33515625px',
							'863.3265624999999px 1034.33515625px',
							'864.6203125px 1035.6109374999999px',
							'864.6203125px 1035.6109374999999px',
							'865.89609375px 1035.71875px',
							'865.89609375px 1035.71875px',
							'867.1359375px 1035.7546874999998px',
							'867.1359375px 1035.7546874999998px',
							'868.3398437499999px 1035.7546874999998px',
							'869.5437499999999px 1035.7546874999998px',
							'869.5437499999999px 1035.7546874999998px',
							'870.7656249999999px 1035.7546874999998px',
							'870.7656249999999px 1035.7546874999998px',
							'871.915625px 1035.7546874999998px',
							'871.915625px 1035.7546874999998px',
							'873.1195312499999px 1035.7546874999998px',
							'873.1195312499999px 1035.7546874999998px',
							'874.3593749999999px 1035.7546874999998px',
							'874.9882812499999px 1036.6171875px',
							'874.9882812499999px 1036.6171875px',
							'875.6351562499999px 1037.4796875px',
							'875.6351562499999px 1037.4796875px',
							'876.8031249999999px 1037.4796875px',
							'876.8031249999999px 1037.4796875px',
							'912.9203124999999px 1037.4796875px',
							'964.3109374999999px 1043.89453125px',
							'1004.5429687499999px 1061.1265624999999px',
							'1781.11640625px 1309.8140624999999px',
							'2513.9898437499996px 1568.1328124999998px',
							'2607.1398437499997px 1602.0039062499998px',
							'2977.7453124999997px 1733.9484375px',
							'3191.7531249999997px 1809.0937499999998px',
							'3243.1437499999997px 1828.3562499999998px',
							'3288.5507812499995px 1842.2460937499998px',
							'3302.60234375px 1850.79921875px',
							'3306.08828125px 1854.2851562499998px',
							'3309.5742187499995px 1856.0281249999998px',
							'3309.5742187499995px 1856.0281249999998px',
							'3311.3171875px 1857.1960937499998px',
							'3311.3171875px 1860.68203125px',
							'3313.0601562499996px 1867.2585937499998px',
							'3315.4679687499997px 1876.9257812499998px',
							'3315.4679687499997px 1890.9773437499998px',
							'3315.4679687499997px 1895.8109375px',
							'3295.6484374999995px 1912.9531249999998px',
							'3265.7124999999996px 1924.1656249999999px',
							'3243.66484375px 1928.5679687499999px',
							'3243.66484375px 1928.5679687499999px',
							'3149.68828125px 1932.9703124999999px',
							'3149.68828125px 1932.9703124999999px',
							'3146.7773437499995px 1932.9703124999999px',
							'3146.7773437499995px 1932.9703124999999px',
							'3145.5015624999996px 1932.9703124999999px',
							'3145.5015624999996px 1932.9703124999999px',
							'3144.8906249999995px 1932.9703124999999px',
						],
						{
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
				}}
			/>
		</>
	);

	return (
		<AbsoluteFill
			showInTimeline={false}
			style={{
				backgroundColor: width < height ? '#1f2427' : '#111518',
				overflow: 'hidden',
			}}
		>
			{width < height ? (
				<div
					style={{
						position: 'absolute',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{/* Continue the track area below the tilted capture, alongside the lighter layer list. */}
					<div
						style={{
							position: 'absolute',
							left: 1224,
							top: 2143,
							width: 3794 - 1224,
							height,
							backgroundColor: '#15171c',
						}}
					/>
				</div>
			) : null}
			{HtmlInCanvas.isSupported() ? (
				<HtmlInCanvas
					name="Canvas Capture"
					width={3794}
					height={2144}
					effects={
						blurEdges === false
							? []
							: [
									radialProgressiveBlur({
										center: [0.25, 0.49],
										width: 0.85,
										height: 0.74,
										start: 0.42,
										startBlur: 0,
										endBlur: 32,
									}),
								]
					}
					durationInFrames={(368 / 60) * fps}
					premountFor={fps}
					style={{
						translate: '150px -430px',
						scale: 0.92,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</HtmlInCanvas>
			) : (
				<AbsoluteFill
					name="Canvas Capture"
					durationInFrames={(368 / 60) * fps}
					premountFor={fps}
					style={{
						width: 3794,
						height: 2144,
						translate: '150px -430px',
						scale: 0.92,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</AbsoluteFill>
			)}
		</AbsoluteFill>
	);
};

export const BasicsPlaybackRatePreview = Interactive.withSchema({
	Component: BasicsPlaybackRateInner,
	componentName: 'BasicsPlaybackRatePreview',
	schema: {},
	wrapInSequence: true,
	layout: 'absolute-fill',
});

export const BasicsPlaybackRateComposition = () => {
	return (
		<Composition
			id="BasicsPlaybackRate"
			component={BasicsPlaybackRatePreview}
			width={1920}
			height={1080}
			defaultProps={{captureStyle: null, blurEdges: null}}
			fps={60}
			durationInFrames={368}
		/>
	);
};
